import {
  BellRing,
  CalendarDays,
  CheckCircle2,
  Clock3,
  Coins,
  History,
  RefreshCw,
  Sparkles,
  Timer,
  Zap,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { thaiTime, thaiTimeShort, workDuration } from "./types";
import { useLanguage } from "@/lib/i18n";

interface MyPulseTabProps {
  myMonthlySummary: any;
}

export function MyPulseTab({ myMonthlySummary }: MyPulseTabProps) {
  const { language } = useLanguage();
  const tx = (th: string, my: string) => language === "my" ? my : th;
  return (
    <div className="employee-module attendance-history-module space-y-5">
      {myMonthlySummary.isLoading && (
        <Card className="glass-panel rounded-3xl p-8 text-center">
          <RefreshCw className="animate-spin text-teal-600 mx-auto mb-3" size={28} />
          <p className="font-black text-slate-900">{tx("กำลังโหลดแดชบอร์ดสรุปผล…", "အနှစ်ချုပ် ဒက်ရှ်ဘုတ်ကို ဖွင့်နေသည်…")}</p>
          <p className="text-xs text-slate-500 mt-1">
            {tx("กำลังประมวลผลข้อมูลการลงเวลาและเงินเดือนของท่าน", "သင့်အလုပ်ချိန်နှင့် လစာဒေတာကို တွက်ချက်နေသည်")}
          </p>
        </Card>
      )}

      {myMonthlySummary.error && (
        <Card className="glass-panel rounded-3xl border-rose-200 bg-rose-50/90 p-6 text-center">
          <p className="font-black text-rose-800">{tx("โหลดข้อมูลสถิติไม่สำเร็จ", "စာရင်းအင်းဒေတာကို ဖွင့်၍မရပါ")}</p>
          <p className="mt-1 text-xs text-rose-600">{myMonthlySummary.error.message}</p>
        </Card>
      )}

      {myMonthlySummary.data && (
        <Card className="glass-panel rounded-[2rem] border-white/90 shadow-xl overflow-hidden">
          <CardHeader className="p-5 sm:p-7 border-b border-slate-100">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-500/10 text-teal-700">
                  <CalendarDays size={24} />
                </div>
                <div>
                  <span className="text-[11px] font-extrabold uppercase tracking-wider text-rose-500 font-numeric">
                    MY MONTHLY PULSE
                  </span>
                  <CardTitle className="text-xl font-black text-slate-900">
                    {tx("แดชบอร์ดสรุปสถิติของฉัน", "ကျွန်ုပ်၏ လစဉ်စာရင်းအင်း")}
                  </CardTitle>
                  <CardDescription className="text-xs font-semibold text-slate-500 mt-0.5">
                    {tx("รอบประจำวันที่", "ကာလ")} {myMonthlySummary.data.startDate} – {myMonthlySummary.data.endDate}
                  </CardDescription>
                </div>
              </div>

              <div className="flex items-center gap-1.5 rounded-full bg-teal-50 px-3 py-1 text-xs font-black text-teal-700 border border-teal-100">
                <BellRing size={13} />
                <span>{tx("LINE แจ้งเตือนอัตโนมัติ", "LINE အလိုအလျောက် အသိပေးချက်")}</span>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-5 sm:p-7 space-y-6">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-2xl bg-white/80 p-4 border border-slate-100 shadow-sm relative overflow-hidden">
                <div className="text-xs font-bold text-slate-500 mb-1">{tx("ลงเวลาแล้ว", "အလုပ်ချိန်မှတ်တမ်း")}</div>
                <div className="text-3xl font-black font-numeric text-slate-900">
                  {myMonthlySummary.data.rows.length}
                </div>
                <div className="text-[11px] font-semibold text-slate-400 mt-1">{tx("วันในรอบนี้", "ယခုကာလအတွင်း ရက်")}</div>
                <div className="absolute right-3 top-3 text-slate-200">
                  <CalendarDays size={28} />
                </div>
              </div>

              <div className="rounded-2xl bg-emerald-50/80 p-4 border border-emerald-100 shadow-sm relative overflow-hidden">
                <div className="text-xs font-bold text-emerald-800 mb-1">{tx("ทำงานครบ", "အလုပ်ချိန်ပြည့်")}</div>
                <div className="text-3xl font-black font-numeric text-emerald-700">
                  {myMonthlySummary.data.payroll.completeDays}
                </div>
                <div className="text-[11px] font-semibold text-emerald-600 mt-1">{tx("วันปกติ", "ပုံမှန်ရက်")}</div>
                <div className="absolute right-3 top-3 text-emerald-200/80">
                  <CheckCircle2 size={28} />
                </div>
              </div>

              <div className="rounded-2xl bg-amber-50/80 p-4 border border-amber-100 shadow-sm relative overflow-hidden">
                <div className="text-xs font-bold text-amber-800 mb-1">{tx("มาสาย", "နောက်ကျ")}</div>
                <div className="text-3xl font-black font-numeric text-amber-700">
                  {myMonthlySummary.data.payroll.lateDays}
                </div>
                <div className="text-[11px] font-semibold text-amber-600 mt-1">{tx("ครั้ง", "ကြိမ်")}</div>
                <div className="absolute right-3 top-3 text-amber-200/80">
                  <Clock3 size={28} />
                </div>
              </div>

              <div className="rounded-2xl bg-rose-50/80 p-4 border border-rose-100 shadow-sm relative overflow-hidden">
                <div className="text-xs font-bold text-rose-800 mb-1">{tx("ประมาณการรายรับ", "ခန့်မှန်းဝင်ငွေ")}</div>
                <div className="text-2xl sm:text-3xl font-black font-numeric text-rose-700">
                  ฿{myMonthlySummary.data.payroll.estimatedGrossPay.toLocaleString()}
                </div>
                <div className="text-[11px] font-semibold text-rose-600 mt-1">{tx("รวม OT แล้ว", "OT အပါအဝင်")}</div>
                <div className="absolute right-3 top-3 text-rose-200/80">
                  <Coins size={28} />
                </div>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl bg-slate-50 p-4 border border-slate-200/80 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal-100 text-teal-700">
                    <Timer size={20} />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-500">{tx("เวลาทำงานรวมทั้งสิ้น", "စုစုပေါင်း အလုပ်ချိန်")}</p>
                    <p className="text-base font-black text-slate-900 font-numeric">
                      {Math.floor(myMonthlySummary.data.payroll.workedMinutes / 60)} {tx("ชม.", "နာရီ")} {" "}
                      {myMonthlySummary.data.payroll.workedMinutes % 60} {tx("นาที", "မိနစ်")}
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl bg-slate-50 p-4 border border-slate-200/80 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                    <Zap size={20} />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-500">{tx("ค่าล่วงเวลา (OT)", "အချိန်ပိုကြေး (OT)")}</p>
                    <p className="text-base font-black text-amber-700 font-numeric">
                      ฿{myMonthlySummary.data.payroll.otPay.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {myMonthlySummary.data.leaveNote && (
              <div className="rounded-2xl bg-blue-50/70 p-3.5 border border-blue-100 text-xs font-semibold text-blue-900 flex items-start gap-2">
                <Sparkles size={16} className="text-blue-600 shrink-0 mt-0.5" />
                <span>{myMonthlySummary.data.leaveNote}</span>
              </div>
            )}

            <div>
              <h3 className="text-sm font-black text-slate-900 mb-3 flex items-center gap-2">
                <History size={16} className="text-teal-600" />
                {tx("รายการลงเวลาในรอบปัจจุบัน", "ယခုကာလ အလုပ်ချိန်မှတ်တမ်း")} ({myMonthlySummary.data.rows.length} {tx("วัน", "ရက်")})
              </h3>

              {myMonthlySummary.data.rows.length > 0 ? (
                <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                  {myMonthlySummary.data.rows.map((row: any) => (
                    <div key={row.id} className="attendance-row">
                      <div>
                        <p className="font-bold text-sm text-slate-900">{row.workDate}</p>
                        <p className="text-xs font-semibold text-slate-500 mt-0.5">
                          {tx("เข้า", "အလုပ်တက်")}: {" "}
                          <span className="font-numeric text-slate-700">
                            {thaiTime(row.checkedInAt)}
                          </span>
                        </p>
                      </div>
                      <div className="text-right">
                        {row.checkedOutAt ? (
                          <>
                            <span className="inline-block rounded-md bg-teal-50 px-2 py-0.5 text-xs font-black text-teal-700 border border-teal-100">
                              {tx("ออก", "အလုပ်ဆင်း")} {thaiTimeShort(row.checkedOutAt)} {tx("น.", "နာရီ")}
                            </span>
                            <p className="text-[11px] font-semibold text-slate-500 mt-0.5">
                              {workDuration(row.checkedInAt, row.checkedOutAt)}
                            </p>
                          </>
                        ) : (
                          <span className="inline-block rounded-md bg-amber-50 px-2 py-0.5 text-xs font-black text-amber-700 border border-amber-100">
                            {tx("ยังไม่เช็คเอาท์", "အလုပ်ဆင်းချိန် မမှတ်ရသေးပါ")}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-2xl bg-slate-50 p-8 text-center text-sm font-semibold text-slate-400">
                  {tx("ยังไม่มีข้อมูลการลงเวลาในรอบปัจจุบัน", "ယခုကာလအတွက် အလုပ်ချိန်မှတ်တမ်း မရှိသေးပါ")}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
